import React, { useEffect, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, store } from './store'
import { UserRole } from './lib/types/userTypes'
import {
  AdminFeatureKey,
  accessMeetsRequired,
  normalizePermissionsFromLogin,
} from './lib/permissions/adminFeatures'
import { INSUFFICIENT_PERMISSION_MESSAGE } from './lib/permissions/uiMessages'
import { hideBanner, showBanner } from './global-state/banner-slice'

interface PrivateRouteProps {
  children: React.ReactNode
  allowedRoles?: UserRole[]
  requiredFeature?: { feature: AdminFeatureKey; minLevel: 'read' | 'write' }
}

interface PublicRouteProps {
  children: React.ReactNode
}

const PrivateRoute = ({ children, allowedRoles, requiredFeature }: PrivateRouteProps) => {
  const dispatch = useDispatch()
  const { authToken, role, permissions } = useSelector((state: RootState) => state.gbcUser)
  const isLoggedIn = authToken && authToken

  const effectivePermissions = useMemo(
    () => permissions ?? normalizePermissionsFromLogin(role as string, null),
    [permissions, role],
  )

  const reqFeature = requiredFeature?.feature
  const reqMin = requiredFeature?.minLevel
  const lacksRequiredFeature =
    reqFeature !== undefined &&
    reqMin !== undefined &&
    !accessMeetsRequired(effectivePermissions[reqFeature], reqMin)

  useEffect(() => {
    if (!isLoggedIn || reqFeature === undefined) {
      return undefined
    }
    if (!lacksRequiredFeature) {
      return undefined
    }
    dispatch(
      showBanner({
        text: INSUFFICIENT_PERMISSION_MESSAGE,
        variant: 'permission-denied-banner',
      }),
    )
    return () => {
      const { variant } = store.getState().banner
      if (variant === 'permission-denied-banner') {
        dispatch(hideBanner())
      }
    }
  }, [dispatch, isLoggedIn, lacksRequiredFeature, reqFeature, reqMin])

  if (!isLoggedIn) {
    return <Navigate to='/login' replace />
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to='/opportunities' replace />
  }

  if (lacksRequiredFeature) {
    return null
  }

  return <>{children}</>
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const { authToken } = useSelector((state: RootState) => state.gbcUser)
  const isLoggedIn = authToken && authToken
  if (!isLoggedIn) return <>{children}</>
  return <Navigate to='/opportunities' replace />
}

export { PrivateRoute, PublicRoute }
